// In src/app/services/ValidationService.ts

import * as Yup from 'yup';
import i18next from 'i18next';
import { getBallastTypeError } from './BallastCbrDatabase';
import { getConvertUnitIdentifier, convertCustomUnit } from './UnitConversionService';
import { unit as unitEnum } from '../enum/project';
import convert from 'convert-units';
import { getIn } from 'formik';
import { logDebug as utilsLogDebug, logError as utilsLogError, LogTag } from '../utils/utils';
import { ProductDataService } from './ProductDataService';
import { isWireInt32, isWireIntegerField } from './WireValueNormalizer';
import { DecimalSurface, decimalErrorMessageKind, isPendingRawText, resolveDecimalFieldPolicy } from '../utils/decimalInput';

/** Table data structure for InfoBox table display */
export interface InfoBoxTable {
    /** Column headers */
    headers: string[];
    /** Table rows - each row is an array of cell values */
    rows: (string | number)[][];
    /** Optional table caption */
    caption?: string;
}

/** Geogrid data structure from API */
interface GeogridData {
    [key: string]:
        | {
              torsionalStiffness?: number;
              [otherProperty: string]: unknown;
          }
        | unknown;
}

/** Yup schema types */
type YupNumberSchema = Yup.NumberSchema<number | undefined>;
type YupStringSchema = Yup.StringSchema<string | undefined>;
type YupObjectSchema = Yup.ObjectSchema<Record<string, unknown>>;

// Cache per i dati dei geogrid per evitare fetch multiple
let geogridDataCache: GeogridData | null = null;
let geogridDataCachePromise: Promise<GeogridData> | null = null;

export class FieldTypeService {
    // Note: This class was previously used for validation schema generation
    // but that functionality has been moved to ValidationService.generateValidationSchema
}

/**
 * Helper function to get geogrid data with cache
 * Now uses ProductDataService (unified database) instead of direct JSON fetch
 * Returns data in backward-compatible format: { "PRODUCT_NAME": { torsionalStiffness, ... } }
 */
const getGeogridDataWithCache = async (): Promise<GeogridData> => {
    if (geogridDataCache) {
        return geogridDataCache;
    }

    if (geogridDataCachePromise) {
        return geogridDataCachePromise;
    }

    geogridDataCachePromise = (async () => {
        try {
            // Get all products from unified database
            const allProducts = ProductDataService.getAllProducts();

            // Convert from { "Family": [ { prodotto: "NAME", ... } ] } to { "NAME": { ... } }
            const flatData: GeogridData = {};
            for (const family of Object.values(allProducts)) {
                for (const product of family) {
                    flatData[product.prodotto] = {
                        torsionalStiffness: product.torsional_stiffness ?? undefined,
                        tensileMax: product.tensile_max ?? undefined,
                        tensile: product.tensile ?? undefined,
                        tensile1Percent: product.tensile_1_percent ?? undefined,
                        tensile2Percent: product.tensile_2_percent ?? undefined,
                        tensile3Percent: product.tensile_3_percent ?? undefined,
                        tensile4Percent: product.tensile_4_percent ?? undefined,
                        tensile5Percent: product.tensile_5_percent ?? undefined,
                    };
                }
            }

            geogridDataCache = flatData;
            return flatData;
        } finally {
            geogridDataCachePromise = null;
        }
    })();

    return geogridDataCachePromise;
};

// Tipi esportati (invariati)
/**
 * FieldConfig describes a dynamic form field and its conditional logic.
 *
 * Conditional visibility:
 * - showWhen: { field: string, value: any }  // Show field only when field === value
 * - hideWhen: { field: string, value: any }  // Hide field when field === value (takes precedence if both present)
 *
 * Rendering logic must be updated to support hideWhen:
 *   - If hideWhen matches, field is hidden (regardless of showWhen)
 *   - If showWhen matches (and hideWhen does not), field is shown
 *   - If neither present, field is always shown
 */
export interface FieldConfig {
    name: string;
    type: 'string' | 'number' | 'select';
    required?: boolean;
    defaultValue?: string | number | boolean;
    label?: string;
    unit?: string | string[];
    renderAs?: 'input' | 'select' | 'textarea' | 'checkbox' | 'radio' | 'custom' | 'toggleButtonGroup' | 'slider';
    options?: Array<{ value: string | number; label: string }>;
    min?: number;
    max?: number;
    step?: number;
    symbol?: string;
    isConvertible?: boolean;
    isReadOnly?: boolean;
    format?: 'integer' | 'float' | 'string'; // Aggiunto per la formattazione
    decimalPlaces?: number; // Aggiunto per specificare il numero di decimali per 'float'
    optionsSource?: string;
    showWhen?: {
        field: string; // Path to the controlling field, e.g., "subgradeInputMethod"
        value: string | number | boolean; // The value the controlling field should have for this field to be displayed
    };
    /**
     * Hide this field when the controlling field equals the specified value.
     * Takes precedence over showWhen if both are present.
     * Example: { field: 'railwayToDesign', value: 'mixed_train' }
     */
    hideWhen?: {
        field: string;
        value: string | number | boolean;
    };
    displayCondition?: {
        field: string; // Path to the controlling field in Formik values, e.g., "type.geogridType"
        value: string | number | boolean; // The value the controlling field should have for this field to be active/displayed
    };
    mutuallyExclusive?: string; // Field name that cannot be 'yes' at the same time as this field
    mutuallyExclusiveGroup?: string[]; // Array of field names that cannot be 'yes' at the same time as this field
    dynamicMin?: {
        sourceField: string; // Field name to get the minimum value from
        sourceGroup: string; // Group name where the source field is located
    };
    layout?: {
        inline?: boolean; // Whether this field should be rendered inline with another field
        width?: string; // CSS width (e.g., '50%', '100px', etc.)
        position?: 'left' | 'right'; // Position when inline
        companion?: string; // Field name to be rendered inline with
    };
    calculation?: (data: FormData) => string | number | boolean; // Function to calculate field value based on other field values
    infoBox?: string; // Optional info text for info popup display
    infoBoxTitle?: string; // Optional title for info popup display
    infoBoxImgSrc?: string; // Optional image URL for info popup display
    infoBoxTableData?: InfoBoxTable; // Optional table data for info popup display
}

// Separate GeoGrid structure from ProjectTypeJson for clarity? Optional but cleaner.
export interface BaseProjectTypeJson {
    projectType: string;
    projectMethod: string;
    fields: Record<string, FieldConfig[]>;
    accordionTitles?: Record<string, string>; // Titoli personalizzati per gli accordion (chiavi di traduzione)
    hiddenGroups?: string[]; // Array di nomi di gruppi da nascondere nella UI
}
export type ProjectTypeJson = BaseProjectTypeJson;
// Use BaseProjectTypeJson for GeoGrid if structure is identical, or define GeoGridTypeJson if it differs
export type GeoGridTypeJson = BaseProjectTypeJson;

/**
 * Classe centralizzata per la validazione dei dati nei progetti
 */
// Helper to check for non-empty value (can be customized)
const isValuePresent = (value: unknown, fieldType: string): boolean => {
    if (value === null || value === undefined) {
        return false;
    }
    if (fieldType === 'string' || fieldType === 'select') {
        return String(value).trim() !== '';
    }
    // For numbers, being present (not null/undefined) is enough,
    // specific range checks are separate (min/max).
    // For boolean, presence is enough.
    return true;
};

/**
 * A lone saved layer without a height was calculated at height 0 (GeogridTtotService fills missing heights
 * with 0), so it opens at 0 rather than as an empty field that blocks RUN. With several layers the
 * calculation moves the heights it has onto the first layers, so 0 would change results: they stay empty.
 */
const openLoneLayerWithoutHeightAtZero = (geogrids: unknown[]): unknown[] => {
    const [onlyLayer] = geogrids;
    if (geogrids.length !== 1 || typeof onlyLayer !== 'object' || onlyLayer === null) return geogrids;
    const { position } = onlyLayer as { position?: unknown };
    return position === undefined || position === null ? [{ ...onlyLayer, position: 0 }] : geogrids;
};

/** typeError message: the separator wording only when the raw text is an ambiguous thousands group. */
const numberTypeErrorMessage =
    (fieldLabel: string) =>
    ({ originalValue }: { originalValue: unknown }): string =>
        decimalErrorMessageKind(originalValue) === 'ambiguous' ? `${fieldLabel} has an ambiguous decimal separator` : `${fieldLabel} must be a number`;

/**
 * Refuses what Yup's cast would accept but the field's decimal input would not commit
 * (an exponent where it is not allowed, a draft with too many decimals, inner spaces) and Infinity.
 */
const withCommittableDecimal = <TSchema extends Yup.NumberSchema<number | null | undefined>>(validator: TSchema, field: FieldConfig, surface: DecimalSurface): TSchema => {
    const fieldLabel = field.label || field.name;
    const policy = resolveDecimalFieldPolicy(field, surface);
    return validator.test(
        'committable-decimal',
        `${fieldLabel} must be a number`,
        (_value, context) =>
            !isPendingRawText(context.originalValue, policy) && (context.originalValue == null || typeof context.originalValue === 'string' || Number.isFinite(context.originalValue))
    ) as TSchema;
};

export class ValidationService {
    /**
     * Optimized deep clone method that avoids JSON.parse/stringify performance issues
     */
    private static deepClone<T>(obj: T): T {
        if (obj === null || typeof obj !== 'object') return obj;
        if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
        if (Array.isArray(obj)) return obj.map(item => this.deepClone(item)) as unknown as T;

        const cloned = {} as T;
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                cloned[key] = this.deepClone(obj[key]);
            }
        }
        return cloned;
    }

    /**
     * Conditional logging that only outputs in development
     */
    private static logDebug(message: string, data?: unknown, tag?: LogTag): void {
        utilsLogDebug('ValidationService', message, data, tag || LogTag.VALIDATION);
    }

    /**
     * Logs errors for critical issues in validation/merge process
     */
    private static logError(message: string, data?: unknown, tag?: LogTag): void {
        utilsLogError('ValidationService', message, data, tag || LogTag.VALIDATION);
    }

    /**
     * Applies proper type coercion with validation
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private static coerceFieldValue(value: unknown, fieldConfig: FieldConfig, groupName: string, fieldName: string): string | number | boolean {
        if (value === undefined || value === null) {
            return fieldConfig.defaultValue ?? (fieldConfig.type === 'number' ? 0 : '');
        }

        if (fieldConfig.type === 'number' && typeof value !== 'number') {
            const numValue = Number(value);
            if (!isNaN(numValue)) {
                return numValue;
            } else {
                return fieldConfig.defaultValue ?? 0;
            }
        } else if (fieldConfig.type === 'string' && typeof value !== 'string') {
            return String(value);
        } else {
            // Ensure we return a valid type
            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                return value;
            }
            return fieldConfig.defaultValue ?? '';
        }
    }

    // --- Helper Function to Create Validator for a Single Field ---
    // Updated to return a more flexible type that can handle nullable modifications
    private static createFieldValidator(
        field: FieldConfig,
        currentUnit: unitEnum,
        isConditionallyHandled = false,
        conversionState?: {
            conversionApplied: boolean;
            originalUnit: unitEnum;
        },
        projectType?: string,
        projectMethod?: string,
        surface: DecimalSurface = 'dynamic'
    ): YupNumberSchema | YupStringSchema {
        const fieldLabel = field.label || field.name;

        switch (field.type) {
            case 'number': {
                let numValidator: Yup.NumberSchema<number | null | undefined> = withCommittableDecimal(
                    Yup.number()
                        .transform((value, originalValue) => {
                            return String(originalValue).trim() === '' ? undefined : value;
                        })
                        .typeError(numberTypeErrorMessage(fieldLabel)),
                    field,
                    surface
                );

                if (field.required && !isConditionallyHandled) {
                    numValidator = numValidator.required(`${fieldLabel} is required`);
                } else if (!isConditionallyHandled) {
                    // Only apply optional if not handled by .when and not field.required
                    numValidator = numValidator.optional().nullable();
                }
                // Unit Conversion for Min/Max - Simplified Logic
                let min = field.min;
                let max = field.max;
                let unitLabel = '';
                if (field.unit && Array.isArray(field.unit)) {
                    unitLabel = currentUnit === unitEnum.SI ? field.unit?.[0] : field.unit?.[1];

                    // Apply simplified conversion logic:
                    // Convert min/max if target is Imperial, conversion not applied, and field is convertible
                    const shouldConvertMinMax =
                        field.isConvertible && currentUnit === unitEnum.Imperial && (!conversionState?.conversionApplied || conversionState?.originalUnit === unitEnum.SI) && field.unit.length === 2;

                    if (shouldConvertMinMax) {
                        const siUnit = getConvertUnitIdentifier(field, false);
                        const impUnit = getConvertUnitIdentifier(field, true);
                        const siLabel = field.unit[0];
                        const impLabel = field.unit[1];
                        unitLabel = impLabel;

                        // Convert min value from metric to Imperial
                        if (typeof min === 'number') {
                            const originalMin = min;
                            let convertedMin: number | null = null;
                            if (siUnit && impUnit && siUnit !== impUnit) {
                                try {
                                    convertedMin = convert(min).from(siUnit).to(impUnit);
                                } catch (e) {}
                            }
                            if (convertedMin === null && siLabel && impLabel && siLabel !== impLabel) {
                                const c = convertCustomUnit(min, siLabel, impLabel);
                                if (c !== min) convertedMin = c;
                            }
                            min = convertedMin !== null ? convertedMin : originalMin;
                        }

                        // Convert max value from metric to Imperial
                        if (typeof max === 'number') {
                            const originalMax = max;
                            let convertedMax: number | null = null;
                            if (siUnit && impUnit && siUnit !== impUnit) {
                                try {
                                    convertedMax = convert(max).from(siUnit).to(impUnit);
                                } catch (e) {}
                            }
                            if (convertedMax === null && siLabel && impLabel && siLabel !== impLabel) {
                                const c = convertCustomUnit(max, siLabel, impLabel);
                                if (c !== max) convertedMax = c;
                            }
                            max = convertedMax !== null ? convertedMax : originalMax;
                        }
                    }
                }
                const unitSuffix = unitLabel ? ` ${unitLabel}` : '';
                if (typeof min === 'number') numValidator = numValidator.min(min, `${fieldLabel} must be at least ${min}${unitSuffix}`);
                if (typeof max === 'number') numValidator = numValidator.max(max, `${fieldLabel} must be at most ${max}${unitSuffix}`);
                // --- End Unit Conversion ---
                return numValidator as YupNumberSchema;
            }
            case 'string':
            case 'select': {
                let stringValidator = Yup.string();
                if (field.required && !isConditionallyHandled) {
                    stringValidator = stringValidator.required(`${fieldLabel} is required`);
                } else if (!isConditionallyHandled) {
                    stringValidator = stringValidator.optional();
                }

                if (projectType === 'railways' && field.name === 'ballastType') {
                    stringValidator = stringValidator.test('supported-ballast', function (value) {
                        const message = getBallastTypeError(value);
                        return message ? this.createError({ message }) : true;
                    });
                }

                // Aggiungi validazione custom per selectedGeogrid - solo per progetti unpaved con metodo giroud
                if (field.name === 'selectedGeogrid' && field.optionsSource === 'unpavedGiroudData' && projectType === 'unpaved' && projectMethod === 'giroud') {
                    stringValidator = stringValidator.test(
                        'torsional-stiffness-validation',
                        'Il geogrid selezionato non è compatibile con il metodo Giroud (torsionalStiffness >= 0.80)',
                        async function (value) {
                            if (!value) return true; // Skip validation se nessun valore selezionato

                            try {
                                const data = await getGeogridDataWithCache();
                                const geogridData = data[value] as { torsionalStiffness?: number } | undefined;

                                if (!geogridData) {
                                    return true; // Allow selection if no data found
                                }

                                const torsionalStiffness = geogridData.torsionalStiffness || 0;

                                if (torsionalStiffness >= 0.8) {
                                    return this.createError({
                                        message: `Il geogrid selezionato "${value}" ha un torsionalStiffness di ${torsionalStiffness}, che è >= 0.80. Non è compatibile con il metodo Giroud.`,
                                    });
                                }

                                return true;
                            } catch (error) {
                                return true; // Allow selection if validation fails
                            }
                        }
                    );
                }

                // Aggiungi validazione per selectedGeotextile
                if (field.name === 'selectedGeotextile' && field.optionsSource === 'unpavedGiroudGeotextiles') {
                    stringValidator = stringValidator.test('geotextile-availability-validation', 'Il geotextile selezionato non è disponibile nel database', async function (value) {
                        if (!value) return true; // Skip validation se nessun valore selezionato

                        try {
                            // Usa la cache condivisa invece di fare un nuovo fetch
                            const data = await getGeogridDataWithCache();
                            const geotextileData = data[value];

                            if (!geotextileData) {
                                return this.createError({
                                    message: `Il geotextile selezionato "${value}" non è disponibile nel database.`,
                                });
                            }

                            return true;
                        } catch (error) {
                            return true; // Allow selection if validation fails
                        }
                    });
                }

                return stringValidator as YupStringSchema;
            }
            default: {
                return Yup.string().optional() as YupStringSchema;
            }
        }
    }
    // --- End Helper Function ---

    /**
     * Creates validation schema for geogrid item fields
     * @param geogridItemConfigs - Field configurations for geogrid items
     * @param currentUnit - Current unit system
     * @param courseType - Type of course for thickness validation (optional)
     * @returns Yup object schema for geogrid items or null if no configs
     */
    private static createGeogridItemSchema(
        geogridItemConfigs: FieldConfig[] | undefined,
        currentUnit: unitEnum,
        courseType?: string,
        conversionState?: {
            conversionApplied: boolean;
            originalUnit: unitEnum;
        },
        projectType?: string,
        projectMethod?: string
    ): YupObjectSchema | null {
        if (!geogridItemConfigs) {
            return null;
        }

        const itemShape: Record<string, Yup.AnySchema> = {};

        geogridItemConfigs.forEach(itemField => {
            if (!itemField.name) return;

            let itemValidator = this.createFieldValidator(itemField, currentUnit, false, conversionState, projectType, projectMethod, 'geogridItem');

            if (itemField.required) {
                itemValidator = itemValidator.required(`${itemField.label ? i18next.t(itemField.label) : itemField.name} is required.`);
            } else {
                itemValidator = this.makeFieldOptional(itemValidator, itemField.type) as YupNumberSchema | YupStringSchema;
            }

            // Add thickness validation for position field if we're in a course context
            if (itemField.name === 'position' && courseType && itemField.type === 'number') {
                itemValidator = (itemValidator as YupNumberSchema).test('position-within-thickness', function (value: number | undefined) {
                    if (typeof value !== 'number') {
                        return true; // Let other validators handle non-number cases
                    }

                    // Navigate up to get the parent group (course) data
                    // this.from[1].value contains the group object (thickness + geogrids array)
                    const parentGroup = this.from?.[1]?.value;

                    let thickness: number | undefined;

                    if (parentGroup && typeof parentGroup === 'object') {
                        thickness = parentGroup.thickness;
                    }

                    if (typeof thickness !== 'number' || thickness <= 0) {
                        return true; // Can't validate if thickness is not set properly
                    }

                    if (value > thickness) {
                        return this.createError({
                            message: `Geogrid position (${value}) cannot exceed ${courseType} thickness (${thickness})`,
                        });
                    }

                    return true;
                });
            }

            itemShape[itemField.name] = itemValidator as unknown as Yup.AnySchema;
        });

        return Yup.object().shape(itemShape);
    }

    /**
     * Makes a field validator optional based on field type
     * @param validator - The field validator
     * @param fieldType - Type of the field
     * @returns Modified validator that is optional
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static makeFieldOptional(validator: Yup.Schema, fieldType: string): Yup.Schema {
        if (fieldType === 'number') {
            return (validator as Yup.NumberSchema).nullable().optional();
        } else if (fieldType === 'string' || fieldType === 'select') {
            return (validator as Yup.StringSchema).optional();
        } else {
            return validator.optional();
        }
    }

    /**
     * Creates validation schema for a single group
     * @param groupName - Name of the group
     * @param groupFieldConfigs - Field configurations for the group
     * @param currentUnit - Current unit system
     * @param geogridItemConfigs - Field configurations for geogrid items
     * @param geogridParentGroups - List of parent groups containing geogrids
     * @param subgradeGroupNames - List of subgrade group names
     * @returns Yup schema for the group
     */
    private static createGroupSchema(
        groupName: string,
        groupFieldConfigs: FieldConfig[],
        currentUnit: unitEnum,
        geogridItemConfigs: FieldConfig[] | undefined,
        geogridParentGroups: string[],
        subgradeGroupNames: string[],
        conversionState?: {
            conversionApplied: boolean;
            originalUnit: unitEnum;
        },
        projectType?: string,
        projectMethod?: string,
        surface: DecimalSurface = 'dynamic'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Yup.Schema<any> {
        const groupShape: Record<string, Yup.AnySchema> = {};
        const geogridArrayFieldName = 'geogrids';

        // Track fields with multiple displayConditions to combine them
        const fieldsWithMultipleConditions = new Map<string, FieldConfig[]>();

        groupFieldConfigs.forEach(field => {
            if (!field.name) return;

            // Skip header fields - they are virtual display elements only
            if (field.name.endsWith('Header') && field.renderAs === 'custom') {
                return;
            }

            // If field has displayCondition and already exists, track for combining
            if (field.displayCondition && groupShape[field.name]) {
                if (!fieldsWithMultipleConditions.has(field.name)) {
                    // Find the original field config
                    const originalField = groupFieldConfigs.find(f => f.name === field.name && !fieldsWithMultipleConditions.get(field.name)?.includes(f));
                    if (originalField) {
                        fieldsWithMultipleConditions.set(field.name, [originalField]);
                    }
                }
                fieldsWithMultipleConditions.get(field.name)?.push(field);
                return; // Don't add to groupShape yet
            }

            const fieldValidator = this.createFieldValidatorWithConditions(
                field,
                currentUnit,
                groupName,
                subgradeGroupNames,
                conversionState,
                projectType,
                projectMethod,
                surface
            ) as unknown as Yup.AnySchema;

            // Counters the backend reads as Int32 (run-text-fields C4): fractions are an inline error, never rounded.
            groupShape[field.name] =
                field.type === 'number' && isWireIntegerField(projectType ?? '', groupName, field.name)
                    ? fieldValidator.test('wire-int32', `${field.label || field.name} must be a whole number`, value => isWireInt32(value))
                    : fieldValidator;
        });

        // Process fields with multiple displayConditions
        fieldsWithMultipleConditions.forEach((fieldConfigs, fieldName) => {
            // Create a combined validator that handles all displayConditions
            const combinedValidator = this.createCombinedConditionalValidator(fieldConfigs, currentUnit, conversionState, projectType, projectMethod, surface);
            groupShape[fieldName] = combinedValidator as unknown as Yup.AnySchema;
        });

        // Handle geogrid parent groups
        if (geogridParentGroups.includes(groupName) && geogridItemConfigs) {
            // Determine course type for validation messages
            const courseType = groupName === 'geogridBaseCourse' ? 'base course' : 'sub-base course';

            // Create geogrid item schema with course-specific validation
            const geogridItemSchema = this.createGeogridItemSchema(geogridItemConfigs, currentUnit, courseType, conversionState, projectType, projectMethod);

            if (geogridItemSchema) {
                const schema = Yup.object().shape({
                    ...groupShape,
                    [geogridArrayFieldName]: Yup.array().of(geogridItemSchema).optional(),
                });

                return schema;
            }
        }

        return Yup.object().shape(groupShape);
    }

    /**
     * Creates field validator with conditional logic applied
     * @param field - Field configuration
     * @param currentUnit - Current unit system
     * @param groupName - Name of the parent group
     * @param subgradeGroupNames - List of subgrade group names
     * @returns Configured field validator
     */
    private static createFieldValidatorWithConditions(
        field: FieldConfig,
        currentUnit: unitEnum,
        groupName: string,
        subgradeGroupNames: string[],
        conversionState?: {
            conversionApplied: boolean;
            originalUnit: unitEnum;
        },
        projectType?: string,
        projectMethod?: string,
        surface: DecimalSurface = 'dynamic'
    ): any {
        const fieldLabel = field.label || field.name;

        // For fields with displayCondition, create a conditional validator that skips all validation rules
        // when the condition is not met
        if (field.displayCondition) {
            return this.createConditionalValidator(field, currentUnit, conversionState, projectType, projectMethod, surface);
        }

        let validator = this.createFieldValidator(field, currentUnit, false, conversionState, projectType, projectMethod, surface);

        const isSpecialSubgradeField = subgradeGroupNames.includes(groupName) && (field.name === 'cbrSubgrade' || field.name === 'deformationModulus');

        if (field.mutuallyExclusive) {
            // Apply mutually exclusive logic
            validator = this.applyMutuallyExclusiveCondition(validator, field, fieldLabel) as YupNumberSchema | YupStringSchema;
        } else if (field.showWhen) {
            // New showWhen logic - most flexible approach
            validator = this.applyShowWhenCondition(validator, field, fieldLabel) as YupNumberSchema | YupStringSchema;
        } else if (isSpecialSubgradeField) {
            validator = this.applySubgradeCondition(validator, field, fieldLabel) as YupNumberSchema | YupStringSchema;
        } else if (field.required) {
            validator = validator.required(`${fieldLabel} is required.`);
        } else {
            validator = this.makeFieldOptional(validator, field.type) as YupNumberSchema | YupStringSchema;
        }

        return validator;
    }

    /**
     * Creates a combined validator for fields with multiple displayConditions (e.g., deckHalfWidth for Rectangular and Railway).
     * Applies the correct validation rules based on which displayCondition is met.
     */
    private static createCombinedConditionalValidator(
        fieldConfigs: FieldConfig[],
        currentUnit: unitEnum,
        conversionState?: {
            conversionApplied: boolean;
            originalUnit: unitEnum;
        },
        projectType?: string,
        projectMethod?: string,
        surface: DecimalSurface = 'dynamic'
    ): Yup.Schema {
        const firstField = fieldConfigs[0];
        const fieldLabel = firstField.label || firstField.name;

        if (firstField.type === 'number') {
            const numberValidator = withCommittableDecimal(
                Yup.number()
                    .transform((value, originalValue) => {
                        return String(originalValue).trim() === '' ? undefined : value;
                    })
                    .typeError(numberTypeErrorMessage(fieldLabel)),
                firstField,
                surface
            )
                .nullable()
                .notRequired();

            return numberValidator.test('combined-conditional-validation', fieldLabel + ' validation', function (this: Yup.TestContext, value: unknown) {
                const rootValues = this.options?.context || this.from?.[0]?.value || {};

                // Find which displayCondition (if any) is met
                let matchedField: FieldConfig | null = null;
                for (const fieldConfig of fieldConfigs) {
                    if (!fieldConfig.displayCondition) {
                        matchedField = fieldConfig;
                        break;
                    }
                    const controllingFieldPath = fieldConfig.displayCondition.field;
                    const expectedValue = fieldConfig.displayCondition.value;
                    const actualValue = getIn(rootValues, controllingFieldPath);

                    if (actualValue === expectedValue) {
                        matchedField = fieldConfig;
                        break;
                    }
                }

                // If no condition matched, skip validation
                if (!matchedField) {
                    return true;
                }

                // Apply validation from the matched field config
                if (matchedField.required && !isValuePresent(value, matchedField.type)) {
                    return this.createError({ message: `${fieldLabel} is required` });
                }

                if (value !== undefined && value !== null) {
                    const numValue = Number(value);
                    if (isNaN(numValue)) {
                        return this.createError({ message: `${fieldLabel} must be a number` });
                    }

                    // Apply min/max from matched config
                    let min = matchedField.min;
                    let max = matchedField.max;
                    let unitLabel = '';

                    // Unit conversion logic (same as createConditionalValidator)
                    if (matchedField.unit && Array.isArray(matchedField.unit)) {
                        unitLabel = currentUnit === unitEnum.SI ? matchedField.unit[0] : matchedField.unit[1];

                        const shouldConvertMinMax =
                            matchedField.isConvertible &&
                            currentUnit === unitEnum.Imperial &&
                            (!conversionState?.conversionApplied || conversionState?.originalUnit === unitEnum.SI) &&
                            matchedField.unit.length === 2;

                        if (shouldConvertMinMax) {
                            const siUnit = getConvertUnitIdentifier(matchedField, false);
                            const impUnit = getConvertUnitIdentifier(matchedField, true);
                            const siLabel = matchedField.unit[0];
                            const impLabel = matchedField.unit[1];
                            unitLabel = impLabel;

                            if (typeof min === 'number') {
                                try {
                                    if (siUnit && impUnit && siUnit !== impUnit) {
                                        min = convert(min).from(siUnit).to(impUnit);
                                    } else if (siLabel && impLabel && siLabel !== impLabel) {
                                        const converted = convertCustomUnit(min, siLabel, impLabel);
                                        if (converted !== min) min = converted;
                                    }
                                } catch (e) {}
                            }

                            if (typeof max === 'number') {
                                try {
                                    if (siUnit && impUnit && siUnit !== impUnit) {
                                        max = convert(max).from(siUnit).to(impUnit);
                                    } else if (siLabel && impLabel && siLabel !== impLabel) {
                                        const converted = convertCustomUnit(max, siLabel, impLabel);
                                        if (converted !== max) max = converted;
                                    }
                                } catch (e) {}
                            }
                        }
                    }

                    const unitSuffix = unitLabel ? ` ${unitLabel}` : '';

                    if (typeof min === 'number' && numValue < min) {
                        return this.createError({ message: `${fieldLabel} must be at least ${min}${unitSuffix}` });
                    }
                    if (typeof max === 'number' && numValue > max) {
                        return this.createError({ message: `${fieldLabel} must be at most ${max}${unitSuffix}` });
                    }
                }

                return true;
            });
        } else {
            // String/select field - similar logic
            const stringValidator = Yup.string().nullable().notRequired();
            return stringValidator.test('combined-conditional-validation', fieldLabel + ' validation', function (this: Yup.TestContext, value: unknown) {
                const rootValues = this.options?.context || this.from?.[0]?.value || {};

                let matchedField: FieldConfig | null = null;
                for (const fieldConfig of fieldConfigs) {
                    if (!fieldConfig.displayCondition) {
                        matchedField = fieldConfig;
                        break;
                    }
                    const controllingFieldPath = fieldConfig.displayCondition.field;
                    const expectedValue = fieldConfig.displayCondition.value;
                    const actualValue = getIn(rootValues, controllingFieldPath);

                    if (actualValue === expectedValue) {
                        matchedField = fieldConfig;
                        break;
                    }
                }

                if (!matchedField) {
                    return true;
                }

                if (matchedField.required && !isValuePresent(value, matchedField.type)) {
                    return this.createError({ message: `${fieldLabel} is required` });
                }

                return true;
            });
        }
    }

    /**
     * Creates a conditional validator that only applies field validation rules when displayCondition is met.
     * This prevents Railway validation rules from being applied to Circular geogrid calculations.
     */
    private static createConditionalValidator(
        field: FieldConfig,
        currentUnit: unitEnum,
        conversionState?: {
            conversionApplied: boolean;
            originalUnit: unitEnum;
        },
        projectType?: string,
        projectMethod?: string,
        surface: DecimalSurface = 'dynamic'
    ): Yup.Schema {
        const fieldLabel = field.label || field.name;
        const controllingFieldPath = field.displayCondition!.field;
        const expectedValueForCondition = field.displayCondition!.value;

        if (field.type === 'number') {
            const numberValidator = withCommittableDecimal(
                Yup.number()
                    .transform((value, originalValue) => {
                        return String(originalValue).trim() === '' ? undefined : value;
                    })
                    .typeError(numberTypeErrorMessage(fieldLabel)),
                field,
                surface
            )
                .nullable()
                .notRequired();

            return numberValidator.test('conditional-field-validation', fieldLabel + ' validation', function (this: Yup.TestContext, value: unknown) {
                const rootValues = this.options?.context || this.from?.[0]?.value || {};
                const actualControllingValue = getIn(rootValues, controllingFieldPath);
                const conditionMet = actualControllingValue === expectedValueForCondition;

                // If displayCondition is not met, skip ALL validation
                if (!conditionMet) {
                    return true;
                }

                // If condition is met, apply full field validation
                if (field.required && !isValuePresent(value, field.type)) {
                    return this.createError({ message: `${fieldLabel} is required` });
                }

                // Apply number-specific validation when condition is met
                if (value !== undefined && value !== null) {
                    const numValue = Number(value);
                    if (isNaN(numValue)) {
                        return this.createError({ message: `${fieldLabel} must be a number` });
                    }

                    // Apply min/max validation with unit conversion if needed
                    let min = field.min;
                    let max = field.max;
                    let unitLabel = '';

                    if (field.unit && Array.isArray(field.unit)) {
                        unitLabel = currentUnit === unitEnum.SI ? field.unit?.[0] : field.unit?.[1];

                        // Apply unit conversion for min/max values if needed
                        const shouldConvertMinMax =
                            field.isConvertible &&
                            currentUnit === unitEnum.Imperial &&
                            (!conversionState?.conversionApplied || conversionState?.originalUnit === unitEnum.SI) &&
                            field.unit.length === 2;

                        if (shouldConvertMinMax) {
                            const siUnit = getConvertUnitIdentifier(field, false);
                            const impUnit = getConvertUnitIdentifier(field, true);
                            const siLabel = field.unit[0];
                            const impLabel = field.unit[1];
                            unitLabel = impLabel;

                            // Convert min value
                            if (typeof min === 'number') {
                                try {
                                    if (siUnit && impUnit && siUnit !== impUnit) {
                                        min = convert(min).from(siUnit).to(impUnit);
                                    } else if (siLabel && impLabel && siLabel !== impLabel) {
                                        const converted = convertCustomUnit(min, siLabel, impLabel);
                                        if (converted !== min) min = converted;
                                    }
                                } catch (e) {
                                    // Keep original value if conversion fails
                                }
                            }

                            // Convert max value
                            if (typeof max === 'number') {
                                try {
                                    if (siUnit && impUnit && siUnit !== impUnit) {
                                        max = convert(max).from(siUnit).to(impUnit);
                                    } else if (siLabel && impLabel && siLabel !== impLabel) {
                                        const converted = convertCustomUnit(max, siLabel, impLabel);
                                        if (converted !== max) max = converted;
                                    }
                                } catch (e) {
                                    // Keep original value if conversion fails
                                }
                            }
                        }
                    }

                    const unitSuffix = unitLabel ? ` ${unitLabel}` : '';

                    if (typeof min === 'number' && numValue < min) {
                        return this.createError({ message: `${fieldLabel} must be at least ${min}${unitSuffix}` });
                    }
                    if (typeof max === 'number' && numValue > max) {
                        return this.createError({ message: `${fieldLabel} must be at most ${max}${unitSuffix}` });
                    }
                }

                return true;
            });
        } else {
            // String/select field
            const stringValidator = Yup.string().nullable().notRequired();

            return stringValidator.test('conditional-field-validation', fieldLabel + ' validation', function (this: Yup.TestContext, value: unknown) {
                const rootValues = this.options?.context || this.from?.[0]?.value || {};
                const actualControllingValue = getIn(rootValues, controllingFieldPath);
                const conditionMet = actualControllingValue === expectedValueForCondition;

                // If displayCondition is not met, skip ALL validation
                if (!conditionMet) {
                    return true;
                }

                // If condition is met, apply full field validation
                if (field.required && !isValuePresent(value, field.type)) {
                    return this.createError({ message: `${fieldLabel} is required` });
                }

                return true;
            });
        }
    }

    /**
     * Applies display condition logic to field validator
     * @param validator - Base field validator
     * @param field - Field configuration
     * @param fieldLabel - Label for the field
     * @returns Modified validator with display condition
     */
    private static applyDisplayCondition(validator: Yup.Schema, field: FieldConfig, fieldLabel: string): Yup.Schema {
        const controllingFieldPath = field.displayCondition!.field;
        const expectedValueForCondition = field.displayCondition!.value;

        // Remove existing validation rules and make field optional/nullable
        validator = (validator as Yup.Schema).notRequired().nullable();

        return validator.test(`conditional-display-${field.name}`, `${fieldLabel} is required`, function (this: Yup.TestContext, value: unknown) {
            const rootValues = this.options?.context || this.from?.[0]?.value || {};
            const actualControllingValue = getIn(rootValues, controllingFieldPath);
            const conditionMet = actualControllingValue === expectedValueForCondition;

            // If displayCondition is not met, skip ALL validation (including min/max rules)
            if (!conditionMet) {
                return true; // Skip validation entirely when condition not met
            }

            // If condition is met, apply original field validation logic
            if (field.required && !isValuePresent(value, field.type)) {
                return this.createError({ message: `${fieldLabel} is required` });
            }

            // Apply min/max validation when condition is met
            if (field.type === 'number' && value !== undefined && value !== null) {
                const numValue = Number(value);
                if (!isNaN(numValue)) {
                    if (typeof field.min === 'number' && numValue < field.min) {
                        return this.createError({ message: `${fieldLabel} must be at least ${field.min}` });
                    }
                    if (typeof field.max === 'number' && numValue > field.max) {
                        return this.createError({ message: `${fieldLabel} must be at most ${field.max}` });
                    }
                }
            }

            return true;
        });
    }

    /**
     * Applies subgrade-specific conditional logic to field validator
     * @param validator - Base field validator
     * @param field - Field configuration
     * @param fieldLabel - Label for the field
     * @returns Modified validator with subgrade condition
     */
    private static applySubgradeCondition(validator: Yup.Schema, field: FieldConfig, fieldLabel: string): Yup.Schema {
        const testName = `subgrade-conditional-${field.name}`;
        const errorMessage = `${fieldLabel} is required based on Subgrade Input Method.`;

        validator = validator.test(testName, errorMessage, function (this: Yup.TestContext, value: unknown) {
            const subgradeGroupValues = this.parent;
            const subgradeInputMethod = subgradeGroupValues?.subgradeInputMethod;

            const conditionMet = (field.name === 'cbrSubgrade' && subgradeInputMethod === 'cbr') || (field.name === 'deformationModulus' && subgradeInputMethod === 'modulus');

            if (conditionMet) {
                return isValuePresent(value, field.type);
            }
            return true;
        });

        // Make nullable if condition isn't met
        if (field.type === 'number') {
            validator = (validator as Yup.NumberSchema).nullable();
        } else if (field.type === 'string' || field.type === 'select') {
            validator = (validator as Yup.StringSchema).optional();
        }

        return validator;
    }

    /**
     * Applies showWhen conditional logic to field validator
     * @param validator - Base field validator
     * @param field - Field configuration
     * @param fieldLabel - Label for the field
     * @param groupName - Name of the parent group
     * @returns Modified validator with showWhen condition
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static applyShowWhenCondition(validator: Yup.Schema, field: FieldConfig, fieldLabel: string): Yup.Schema {
        const controllingFieldName = field.showWhen!.field;
        const expectedValue = field.showWhen!.value;
        const testName = `show-when-conditional-${field.name}`;
        const errorMessage = `${fieldLabel} is required when ${controllingFieldName} is ${expectedValue}.`;

        validator = validator.test(testName, errorMessage, function (this: Yup.TestContext, value: unknown) {
            // Get the controlling field value from the same group
            const groupValues = this.parent;
            const controllingFieldValue = groupValues?.[controllingFieldName];

            // Check if the condition is met
            const conditionMet = controllingFieldValue === expectedValue;

            if (conditionMet) {
                // Field should be visible and required (if originally required)
                if (field.required && !isValuePresent(value, field.type)) {
                    return this.createError({ message: errorMessage });
                }
                return true;
            } else {
                // Field is not visible, so it's always valid regardless of value
                return true;
            }
        });

        // Make the field nullable/optional when condition isn't met
        if (field.type === 'number') {
            validator = (validator as Yup.NumberSchema).nullable();
        } else if (field.type === 'string' || field.type === 'select') {
            validator = (validator as Yup.StringSchema).optional();
        }

        return validator;
    }

    /**
     * Applies mutually exclusive logic to field validator
     * @param validator - Base field validator
     * @param field - Field configuration
     * @param fieldLabel - Label for the field
     * @param groupName - Name of the parent group
     * @returns Modified validator with mutually exclusive condition
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static applyMutuallyExclusiveCondition(validator: Yup.Schema, field: FieldConfig, fieldLabel: string): Yup.Schema {
        const mutuallyExclusiveFieldName = field.mutuallyExclusive!;
        const testName = `mutually-exclusive-${field.name}`;
        const errorMessage = `${fieldLabel} and ${mutuallyExclusiveFieldName} cannot both be 'yes' at the same time.`;

        validator = validator.test(testName, errorMessage, function (this: Yup.TestContext, value: unknown) {
            // Get the mutually exclusive field value from the same group
            const groupValues = this.parent;
            const mutuallyExclusiveFieldValue = groupValues?.[mutuallyExclusiveFieldName];

            // Check if both fields are set to 'yes'
            if (value === 'yes' && mutuallyExclusiveFieldValue === 'yes') {
                return this.createError({ message: errorMessage });
            }

            // If this field is required and not the mutually exclusive case, validate normally
            if (field.required && !isValuePresent(value, field.type)) {
                return this.createError({ message: `${fieldLabel} is required.` });
            }

            return true;
        });

        // Make the field nullable/optional for flexibility
        if (field.type === 'string' || field.type === 'select') {
            validator = (validator as Yup.StringSchema).optional();
        }

        return validator;
    }

    /**
     * Generates a complete validation schema for project type configuration
     * @param projectTypeJson - Project type configuration
     * @param currentUnit - Current unit system (SI or Imperial)
     * @returns Yup object schema for validation
     */
    static generateValidationSchema(
        projectTypeJson: ProjectTypeJson | GeoGridTypeJson,
        currentUnit: unitEnum,
        conversionState?: {
            conversionApplied: boolean;
            originalUnit: unitEnum;
        },
        surface: DecimalSurface = 'dynamic'
    ): Yup.ObjectSchema<any> {
        this.logDebug(`[ValidationService] Generating validation schema for ${projectTypeJson.projectType}-${projectTypeJson.projectMethod}-${currentUnit}`);

        const configFields = projectTypeJson.fields;
        const geogridParentGroups = ['geogridBaseCourse', 'geogridSubBaseCourse'];
        const geogridItemConfigKey = 'geogridItem';
        const subgradeGroupNames = ['subgrade'];

        // Get geogrid item configurations
        const geogridItemConfigs = configFields[geogridItemConfigKey];

        // Create schemas for each group
        const groupSchemas: Record<string, Yup.Schema<any>> = {};

        Object.entries(configFields).forEach(([groupName, groupFieldConfigs]) => {
            if (groupName === geogridItemConfigKey) {
                return; // Skip the item config key itself
            }

            groupSchemas[groupName] = this.createGroupSchema(
                groupName,
                groupFieldConfigs,
                currentUnit,
                geogridItemConfigs,
                geogridParentGroups,
                subgradeGroupNames,
                conversionState,
                projectTypeJson.projectType,
                projectTypeJson.projectMethod,
                surface
            );
        });

        return Yup.object()
            .shape(groupSchemas)
            .test('context-setup', 'Setup context', function (value) {
                // Set the context for nested validations to access root values
                this.options.context = value;
                return true;
            });
    }

    // buildInitialDataStructure MUST match the OLD config structure now
    static buildInitialDataStructure(projectTypeJson: ProjectTypeJson | GeoGridTypeJson): Record<string, any> {
        this.logDebug('[ValidationService] Building initial data using OLD structure logic');
        const initialData: Record<string, any> = {};
        const geogridParentGroups = ['geogridBaseCourse', 'geogridSubBaseCourse']; // Groups containing geogrids array
        const geogridItemConfigKey = 'geogridItem'; // Key holding the config for items
        const geogridArrayFieldName = 'geogrids'; // Name of the array field WITHIN the parent object

        Object.entries(projectTypeJson.fields).forEach(([groupName, groupFields]) => {
            // Skip the item config key itself
            if (groupName === geogridItemConfigKey) {
                return;
            }

            // Initialize group object
            initialData[groupName] = {};

            // Assign defaults for the group's own fields
            groupFields.forEach(field => {
                // Skip header fields - they are virtual display elements only
                if (field.name.endsWith('Header') && field.renderAs === 'custom') {
                    return;
                }

                let defaultValue = field.defaultValue;
                if (defaultValue === undefined) {
                    defaultValue = field.type === 'number' ? 0 : '';
                }
                initialData[groupName][field.name] = defaultValue;
            });

            // If it's a parent group, initialize the nested array
            if (geogridParentGroups.includes(groupName)) {
                initialData[groupName][geogridArrayFieldName] = []; // Initialize empty array
            }
        });

        this.logDebug('[ValidationService] Built initial structure:', initialData);
        return initialData;
    }

    static validateDynamicData(data: Record<string, any> | undefined | null, projectTypeJson: ProjectTypeJson | GeoGridTypeJson): Record<string, any> {
        this.logDebug('[ValidationService] Validating/Merging data using OLD structure logic');

        // Use the buildInitialDataStructure that now creates the correct nested structure
        const initialStructure = ValidationService.buildInitialDataStructure(projectTypeJson);
        if (!data) {
            this.logDebug('[ValidationService] No existing data provided, returning default structure.');
            return initialStructure;
        }

        // Initialize validated data with empty group objects (NOT defaults)
        // Defaults will only be applied for required fields that are missing from API data
        const validatedData: Record<string, any> = {};
        Object.keys(initialStructure).forEach(groupName => {
            validatedData[groupName] = {};
        });

        const geogridParentGroups = ['geogridBaseCourse', 'geogridSubBaseCourse']; // Groups containing geogrids array
        const geogridItemConfigKey = 'geogridItem';
        const geogridArrayFieldName = 'geogrids';

        /**
         * Helper to get default value for a field
         */
        const getDefaultValue = (fieldConfig: FieldConfig): any => {
            if (fieldConfig.defaultValue !== undefined) {
                return fieldConfig.defaultValue;
            }
            return fieldConfig.type === 'number' ? 0 : '';
        };

        /**
         * Helper to process a field - applies value or default based on required flag
         * @param existingGroupData - Data from API/backend for this group
         * @param fieldConfig - Field configuration
         * @param groupName - Name of the group
         */
        const processField = (existingGroupData: Record<string, any> | undefined, fieldConfig: FieldConfig, groupName: string): void => {
            // Skip header fields - they are virtual display elements only
            if (fieldConfig.name.endsWith('Header') && fieldConfig.renderAs === 'custom') {
                return;
            }

            const fieldName = fieldConfig.name;

            if (existingGroupData && existingGroupData.hasOwnProperty(fieldName)) {
                // Field exists in API data → use the value (even if empty/0/undefined)
                const value = existingGroupData[fieldName];
                validatedData[groupName][fieldName] = this.coerceFieldValue(value, fieldConfig, groupName, fieldName);
            } else if (fieldConfig.required || fieldConfig.defaultValue !== undefined) {
                // Apply default if:
                // 1. Field is required (safety net)
                // 2. Field has a configured defaultValue (even if optional, e.g., CBR)
                validatedData[groupName][fieldName] = getDefaultValue(fieldConfig);
            }
            // Only truly optional fields with NO defaultValue remain undefined
        };

        Object.keys(initialStructure).forEach(groupName => {
            // Skip the item config key if present in initialStructure (it shouldn't be)
            if (groupName === geogridItemConfigKey) return;

            const groupFieldConfigs = projectTypeJson.fields[groupName];
            const existingGroupData = data && data.hasOwnProperty(groupName) && data[groupName] !== undefined && data[groupName] !== null
                ? data[groupName]
                : undefined;

            if (geogridParentGroups.includes(groupName)) {
                // Handle parent groups (process parent fields + array)
                if (existingGroupData && typeof existingGroupData === 'object' && !Array.isArray(existingGroupData)) {
                    // Process parent fields
                    groupFieldConfigs.forEach(fieldConfig => {
                        processField(existingGroupData, fieldConfig, groupName);
                    });
                    // Merge geogrids array
                    if (existingGroupData.hasOwnProperty(geogridArrayFieldName) && Array.isArray((existingGroupData as any)[geogridArrayFieldName])) {
                        // Clone the array to prevent reference sharing
                        validatedData[groupName][geogridArrayFieldName] = this.deepClone((existingGroupData as any)[geogridArrayFieldName]);
                    } else {
                        // Initialize empty geogrids array for geogrid parent groups
                        validatedData[groupName][geogridArrayFieldName] = [];
                    }
                } else {
                    // No existing data for this group - apply defaults for required fields only
                    groupFieldConfigs.forEach(fieldConfig => {
                        processField(undefined, fieldConfig, groupName);
                    });
                    // Initialize empty geogrids array
                    validatedData[groupName][geogridArrayFieldName] = [];
                }
            } else {
                // Handle simple groups
                if (existingGroupData && typeof existingGroupData === 'object' && !Array.isArray(existingGroupData)) {
                    // Process fields with existing data
                    groupFieldConfigs.forEach(fieldConfig => {
                        processField(existingGroupData, fieldConfig, groupName);
                    });
                } else {
                    // No existing data for this group - apply defaults for required fields only
                    groupFieldConfigs.forEach(fieldConfig => {
                        processField(undefined, fieldConfig, groupName);
                    });
                }
            }
        });
        this.logDebug('[ValidationService] Merged/Validated Data:', validatedData);

        // 🔍 VALIDATION: Verify merge success for critical fields
        // Prevents silent data loss if merge fails (e.g., structure mismatch, schema evolution issues)
        this.validateMergeSuccess(data, validatedData, initialStructure);

        return validatedData;
    }

    /**
     * Validates that critical fields were successfully merged from saved data.
     * Logs errors if saved values were lost during merge process.
     *
     * @param data - Original saved data from backend/Redux
     * @param validatedData - Result after merge with defaults
     * @param initialStructure - Default structure with initial values
     * @private
     */
    private static validateMergeSuccess(data: Record<string, any> | undefined | null, validatedData: Record<string, any>, initialStructure: Record<string, any>): void {
        if (!data) return; // No saved data to validate

        // Define critical fields by project type/structure
        const criticalFieldsByGroup: Record<string, string[]> = {
            // Railway critical fields
            loadData: ['designAxleLoad', 'trainSpeed'],
            // Paved critical fields
            trafficLoad: ['esal_final_value', 'reliability_level_r'],
            // Common fields
            roadInfo: ['carriagewayWidth', 'longitudinalSlope'],
        };

        Object.entries(criticalFieldsByGroup).forEach(([groupName, fieldNames]) => {
            const savedGroup = data[groupName];
            const mergedGroup = validatedData[groupName];
            const defaultGroup = initialStructure[groupName];

            if (!savedGroup || typeof savedGroup !== 'object') return; // Group not in saved data

            fieldNames.forEach(fieldName => {
                const savedValue = savedGroup[fieldName];
                const mergedValue = mergedGroup?.[fieldName];
                const defaultValue = defaultGroup?.[fieldName];

                // Check if saved value exists but was not merged correctly
                if (savedValue !== undefined && savedValue !== null) {
                    // Type coercion might change type (string "900" → number 900)
                    // So compare with loose equality for numeric values
                    const savedNumeric = typeof savedValue === 'string' ? parseFloat(savedValue) : savedValue;
                    const mergedNumeric = typeof mergedValue === 'string' ? parseFloat(mergedValue) : mergedValue;

                    if (savedNumeric !== mergedNumeric) {
                        // Merge failed: saved value lost
                        this.logError(`[ValidationService] ⚠️ MERGE FAILED for ${groupName}.${fieldName}`, {
                            savedValue,
                            mergedValue,
                            defaultValue,
                            lostData: savedValue !== defaultValue,
                            message: 'Saved value was not preserved during merge - potential data loss!',
                        });
                    }
                }
            });
        });
    }

    /**
     * Generates a Yup validation schema specifically for GeoGrid components.
     * This method is similar to generateValidationSchema and can be expanded for GeoGrid specific needs.
     * @param {ProjectTypeJson | GeoGridTypeJson} projectTypeJson - The configuration JSON for the GeoGrid type.
     * @param {unitEnum} currentUnit - The current unit system (SI or Imperial).
     * @returns {Yup.ObjectSchema<any>} A Yup object schema for validating GeoGrid data.
     */
    static generateGeoGridValidationSchema(
        projectTypeJson: ProjectTypeJson | GeoGridTypeJson,
        currentUnit: unitEnum,
        conversionState?: { conversionApplied: boolean; originalUnit: unitEnum }
    ): Yup.ObjectSchema<any> {
        this.logDebug(
            `[ValidationService] Calling generateGeoGridValidationSchema (delegating to generateValidationSchema) for ${projectTypeJson.projectType}-${projectTypeJson.projectMethod}-${currentUnit}`
        );
        // For now, it delegates to the existing generic schema generation.
        // It can be customized further if GeoGrid has unique validation aspects not covered by the generic one.
        return this.generateValidationSchema(projectTypeJson, currentUnit, conversionState, 'geogridScalar');
    }

    /**
     * Builds an initial data structure specifically for GeoGrid values,
     * considering existing data and whether it's a new GeoGrid setup.
     * @param {ProjectTypeJson | GeoGridTypeJson} config - The configuration JSON (e.g., from getGeoGridJson).
     * @param {Record<string, any> | null} [existingGeoGridData] - Optional existing GeoGrid data to populate from.
     * @param {boolean} [isNewGeoGrid] - Optional flag, true if this is a new GeoGrid setup (influences default population).
     * @returns {Record<string, any>} An object representing the initial values for the GeoGrid form.
     */
    static buildInitialGeoGridValues(config: ProjectTypeJson | GeoGridTypeJson, existingGeoGridData?: Record<string, any> | null, isNewGeoGrid?: boolean): Record<string, any> {
        this.logDebug('[ValidationService] Building initial GeoGrid values with detailed logic.');
        const initialValues: Record<string, any> = {};
        const configFields = config.fields;

        // Helper function to evaluate display conditions
        const evaluateDisplayCondition = (condition: { field: string; value: any }, currentValues: Record<string, any>): boolean => {
            const fieldPath = condition.field.split('.');
            let currentObj = currentValues;

            // Navigate through nested object path
            for (const segment of fieldPath) {
                if (!currentObj || typeof currentObj !== 'object' || !(segment in currentObj)) {
                    return false;
                }
                currentObj = currentObj[segment];
            }

            return currentObj === condition.value;
        };

        // Pre-populate critical reference values (like geogridType) from existing data
        // This ensures display condition evaluation works correctly
        if (existingGeoGridData?.type?.geogridType) {
            initialValues.type = { geogridType: existingGeoGridData.type.geogridType };
            this.logDebug(`[ValidationService] Pre-populated type.geogridType from existing data:`, existingGeoGridData.type.geogridType);
        }

        const geogridParentGroups = ['geogridBaseCourse', 'geogridSubBaseCourse'];
        const geogridItemConfigKey = 'geogridItem';
        const geogridArrayFieldName = 'geogrids';
        const geogridItemConfigs = configFields[geogridItemConfigKey];

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const buildDefaultGeogridItem = (): Record<string, unknown> => {
            const defaultItem: Record<string, unknown> = {};
            if (geogridItemConfigs) {
                geogridItemConfigs.forEach(f => {
                    defaultItem[f.name] = f.defaultValue ?? (f.type === 'number' ? 0 : '');
                });
            }
            return defaultItem;
        };

        for (const groupName in configFields) {
            if (!Object.prototype.hasOwnProperty.call(configFields, groupName) || groupName === geogridItemConfigKey) {
                continue;
            }

            const currentGroupFieldConfigs = configFields[groupName];
            const existingGroupDataForThisKey = existingGeoGridData?.[groupName];
            initialValues[groupName] = {};

            if (geogridParentGroups.includes(groupName)) {
                // Handle parent fields (e.g., thickness)
                currentGroupFieldConfigs.forEach(parentField => {
                    const fieldName = parentField.name;
                    let valueToUse = parentField.defaultValue ?? (parentField.type === 'number' ? 0 : ''); // Default first

                    if (
                        !isNewGeoGrid &&
                        existingGroupDataForThisKey &&
                        typeof existingGroupDataForThisKey === 'object' &&
                        !Array.isArray(existingGroupDataForThisKey) &&
                        existingGroupDataForThisKey.hasOwnProperty(fieldName)
                    ) {
                        const _existingValue = existingGroupDataForThisKey[fieldName as keyof typeof existingGroupDataForThisKey];
                        if (typeof _existingValue !== 'undefined') {
                            valueToUse = _existingValue;
                        }
                        // If _existingValue is undefined, valueToUse remains parentField.defaultValue
                    }
                    initialValues[groupName][fieldName] = valueToUse;
                });

                // Handle the geogrids array
                let geogridsArrayToUse: unknown[] = [];
                if (
                    !isNewGeoGrid && // Only use existing if not explicitly a new setup
                    existingGroupDataForThisKey &&
                    typeof existingGroupDataForThisKey === 'object' &&
                    !Array.isArray(existingGroupDataForThisKey) &&
                    geogridArrayFieldName in existingGroupDataForThisKey &&
                    Array.isArray(existingGroupDataForThisKey[geogridArrayFieldName])
                ) {
                    geogridsArrayToUse = openLoneLayerWithoutHeightAtZero(existingGroupDataForThisKey[geogridArrayFieldName]);
                } else if (isNewGeoGrid) {
                    if (['geogridBaseCourse', 'geogridSubBaseCourse'].includes(groupName)) {
                        geogridsArrayToUse = [];
                        this.logDebug(`[ValidationService] Initializing empty geogrids array for new project in special group ${groupName}.`);
                    } else {
                        geogridsArrayToUse = [];
                        this.logDebug(`[ValidationService] Initializing empty geogrids array for new project in group ${groupName} as per convention.`);
                    }
                }
                initialValues[groupName][geogridArrayFieldName] = geogridsArrayToUse;
            } else {
                // Normal groups - process fields with display condition checking
                currentGroupFieldConfigs.forEach(f => {
                    // Check if field should be displayed based on display condition
                    if (f.displayCondition && !evaluateDisplayCondition(f.displayCondition, initialValues)) {
                        // Skip this field if display condition is not met
                        this.logDebug(`[ValidationService] Skipping field '${f.name}' due to unmet display condition:`, f.displayCondition);
                        return;
                    }

                    let valueToUse = f.defaultValue ?? (f.type === 'number' ? 0 : ''); // Default first
                    if (!isNewGeoGrid && existingGroupDataForThisKey && typeof existingGroupDataForThisKey === 'object' && !Array.isArray(existingGroupDataForThisKey)) {
                        if (existingGroupDataForThisKey.hasOwnProperty(f.name)) {
                            const _existingValue = existingGroupDataForThisKey[f.name as keyof typeof existingGroupDataForThisKey];
                            if (typeof _existingValue !== 'undefined') {
                                // Only use existing value if it's not undefined
                                valueToUse = _existingValue;
                            }
                            // If _existingValue is undefined, valueToUse remains f.defaultValue
                        }
                    }
                    initialValues[groupName][f.name] = valueToUse;
                    this.logDebug(`[ValidationService] Included field '${f.name}' with value:`, valueToUse);
                });
            }
        }
        this.logDebug('[ValidationService] Built initial GeoGrid values:', initialValues);
        return initialValues;
    }

    /**
     * Handles mutually exclusive field logic for form components
     * @param currentValues - Current form values for the group
     * @param changedFieldName - Name of the field that was just changed
     * @param newValue - New value for the changed field
     * @param fieldConfigs - Field configurations for the group
     * @returns Updated values object with mutually exclusive logic applied
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static handleMutuallyExclusiveFields(currentValues: Record<string, unknown>, changedFieldName: string, newValue: unknown, fieldConfigs: FieldConfig[]): Record<string, unknown> {
        const updatedValues = { ...currentValues };
        updatedValues[changedFieldName] = newValue;

        // Find the field configuration for the changed field
        const changedFieldConfig = fieldConfigs.find(field => field.name === changedFieldName);

        if (newValue === 'yes') {
            // Handle mutuallyExclusiveGroup (multiple fields)
            if (changedFieldConfig?.mutuallyExclusiveGroup) {
                changedFieldConfig.mutuallyExclusiveGroup.forEach(mutuallyExclusiveFieldName => {
                    if (mutuallyExclusiveFieldName !== changedFieldName) {
                        updatedValues[mutuallyExclusiveFieldName] = 'no';
                        this.logDebug(`[ValidationService] Applied mutually exclusive group logic: ${changedFieldName}='yes' -> ${mutuallyExclusiveFieldName}='no'`);
                    }
                });
            }
            // Handle single mutuallyExclusive field (backward compatibility)
            else if (changedFieldConfig?.mutuallyExclusive) {
                const mutuallyExclusiveFieldName = changedFieldConfig.mutuallyExclusive;
                updatedValues[mutuallyExclusiveFieldName] = 'no';
                this.logDebug(`[ValidationService] Applied mutually exclusive logic: ${changedFieldName}='yes' -> ${mutuallyExclusiveFieldName}='no'`);
            }
        }

        return updatedValues;
    }
}
